import {
  createSubmissionResponseSchema,
  errorBodySchema,
  publicConfigResponseSchema,
  releaseResponseSchema,
  sessionResponseSchema,
  type Answers,
  type ErrorCode,
} from "@ptp/contracts";
import { env } from "./env.js";
import { clientId } from "./session.js";

/** A failed request carrying the server's error code, so the UI can react to it. */
export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode | "NETWORK",
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request(path: string, init: RequestInit = {}): Promise<unknown> {
  let response: Response;
  try {
    response = await fetch(`${env.apiBaseUrl}${path}`, {
      ...init,
      // Same-origin with the API behind the reverse proxy, so the session cookie is
      // first-party and needs no CORS handling.
      credentials: "same-origin",
      headers: {
        "content-type": "application/json",
        "x-client-id": clientId(),
        ...init.headers,
      },
    });
  } catch {
    throw new ApiError("NETWORK", "Could not reach the server.");
  }

  const text = await response.text();
  const body: unknown = text.length > 0 ? JSON.parse(text) : {};

  if (!response.ok) {
    const parsed = errorBodySchema.safeParse(body);
    if (parsed.success) throw new ApiError(parsed.data.error.code, parsed.data.error.message);
    throw new ApiError("INTERNAL", `Request failed (${response.status}).`);
  }
  return body;
}

export async function startSession() {
  return sessionResponseSchema.parse(await request("/session", { method: "POST" }));
}

export async function fetchPublicConfig() {
  return publicConfigResponseSchema.parse(await request("/config"));
}

export async function createSubmission(answers: Answers, idempotencyKey: string) {
  return createSubmissionResponseSchema.parse(
    await request("/submissions", {
      method: "POST",
      headers: { "idempotency-key": idempotencyKey },
      body: JSON.stringify({ answers, clientId: clientId() }),
    }),
  );
}

export async function releaseSubmission(submissionId: string) {
  return releaseResponseSchema.parse(
    await request(`/submissions/${submissionId}/release`, { method: "POST" }),
  );
}
